import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AVATAR_STYLES, type AvatarStyle } from '@evi/shared';
import { BottomSheet } from '@evi/ui';
import { setAvatar, buildAvatarUrl } from '@/lib/api';

interface AvatarPickerProps {
  currentStyle: AvatarStyle;
  currentSeed: string;
  onClose: () => void;
}

interface PickerItem {
  id: string;
  style: AvatarStyle;
  seed: string;
  name: string;
  /** Generated-by-shuffle avatars are flagged so we can visually set them apart. */
  generated?: boolean;
}

/**
 * Preset palette — one recognisable avatar per offered DiceBear style, in the
 * canonical AVATAR_STYLES order (Neutral sets lead), so the first tile is the
 * default. The style ids come from the single source of truth in @evi/shared;
 * seeds are arbitrary stable strings. When the PWA's design system gets real
 * preset PNGs, swap this for a `<img src="/avatars/...">` grid — the
 * `setAvatar(style, seed)` API already accepts strings.
 */
const PRESETS: PickerItem[] = [
  { id: 'adventurer-neutral', style: 'adventurer-neutral', seed: 'evi-adventurer', name: 'Adventurer' },
  { id: 'avataaars-neutral',  style: 'avataaars-neutral',  seed: 'evi-avataaars',  name: 'Avataaars' },
  { id: 'big-ears',           style: 'big-ears',           seed: 'evi-big-ears',   name: 'Big Ears' },
  { id: 'big-ears-neutral',   style: 'big-ears-neutral',   seed: 'evi-big-ears-n', name: 'Big Ears Neutral' },
  { id: 'big-smile',          style: 'big-smile',          seed: 'evi-big-smile',  name: 'Big Smile' },
  { id: 'bottts-neutral',     style: 'bottts-neutral',     seed: 'evi-bottts',     name: 'Bottts' },
  { id: 'lorelei-neutral',    style: 'lorelei-neutral',    seed: 'evi-lorelei',    name: 'Lorelei' },
];

// Randomise across the offered styles only — never outside AVATAR_STYLES.
const SHUFFLE_STYLES: AvatarStyle[] = [...AVATAR_STYLES];

function randomSeed(): string {
  // crypto.randomUUID is available in modern browsers; fallback keeps tests happy.
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID().slice(0, 12);
  }
  return Math.random().toString(36).slice(2, 14);
}

function makeGenerated(): PickerItem {
  const style = SHUFFLE_STYLES[Math.floor(Math.random() * SHUFFLE_STYLES.length)] ?? AVATAR_STYLES[0];
  const seed = randomSeed();
  return {
    id: `gen-${seed}`,
    style,
    seed,
    name: 'Generated',
    generated: true,
  };
}

/**
 * Avatar picker, rendered on the shared @evi/ui BottomSheet so it gets
 * swipe-down-to-dismiss + a drag handle (plus ESC, backdrop tap, and
 * focus trap) like the app's other sheets, rather than the bespoke
 * overlay this replaced. Tapping a preset calls the
 * `/gamification/avatar` endpoint and closes the sheet; React Query
 * invalidates the profile so the Profile page shows the new avatar.
 *
 * A "Generate new" button above the presets rolls a random
 * `(style, seed)` pair and stacks it into a "Generated" row above
 * the fixed presets so users can compare several rolls before
 * committing.
 *
 * All surfaces read `--evi-*` tokens so the sheet reskins alongside
 * the active theme.
 */
export function AvatarPicker({ currentStyle, currentSeed, onClose }: AvatarPickerProps) {
  const queryClient = useQueryClient();
  const [pending, setPending] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [generated, setGenerated] = useState<PickerItem[]>([]);

  const mutation = useMutation({
    mutationFn: ({ style, seed }: { style: AvatarStyle; seed: string }) => setAvatar(style, seed),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile'] });
      onClose();
    },
    onError: (e: unknown) => {
      setError(e instanceof Error ? e.message : 'Failed to update avatar.');
      setPending(null);
    },
  });

  function handleSelect(p: PickerItem): void {
    setPending(p.id);
    setError('');
    mutation.mutate({ style: p.style, seed: p.seed });
  }

  function handleShuffle(): void {
    setGenerated((prev) => [makeGenerated(), ...prev].slice(0, 8));
  }

  return (
    <BottomSheet isOpen onClose={onClose} title="Choose avatar" glass>
      {/* "Generate new" moved into the body: the shared BottomSheet's own
          header carries the "Choose avatar" title + close X, and provides
          the swipe-down-to-dismiss gesture + drag handle the bespoke
          overlay this replaced never had. */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 14 }}>
        <button
          type="button"
          onClick={handleShuffle}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '8px 14px',
            borderRadius: 999,
            background: 'var(--evi-surface-2)',
            border: '1px solid var(--evi-border)',
            color: 'var(--evi-text)',
            fontSize: 12,
            fontWeight: 500,
            fontFamily: 'inherit',
            cursor: 'pointer',
            transition: 'border-color 0.15s ease, background 0.15s ease',
          }}
        >
          <ShuffleIcon />
          Generate new
        </button>
      </div>

      {error && (
        <div
          role="alert"
          style={{
            padding: '8px 12px',
            borderRadius: 8,
            background: 'var(--evi-accent-soft, rgba(34, 197, 94, 0.1))',
            border: '1px solid var(--evi-warn)',
            color: 'var(--evi-warn)',
            fontSize: 12,
            marginBottom: 12,
          }}
        >
          {error}
        </div>
      )}

      {generated.length > 0 && (
        <>
          <SectionLabel>Generated</SectionLabel>
          <Grid
            items={generated}
            currentStyle={currentStyle}
            currentSeed={currentSeed}
            pending={pending}
            onSelect={handleSelect}
          />
          <div style={{ height: 14 }} />
          <SectionLabel>Presets</SectionLabel>
        </>
      )}

      <Grid
        items={PRESETS}
        currentStyle={currentStyle}
        currentSeed={currentSeed}
        pending={pending}
        onSelect={handleSelect}
      />

      <p
        style={{
          marginTop: 16,
          marginBottom: 0,
          fontSize: 11,
          color: 'var(--evi-text-mute)',
          textAlign: 'center',
          letterSpacing: 0.5,
          textTransform: 'uppercase',
          fontWeight: 500,
        }}
      >
        Pick one. Your avatar syncs to every device.
      </p>
    </BottomSheet>
  );
}

function SectionLabel({ children }: { children: string }) {
  return (
    <div
      style={{
        fontSize: 10,
        letterSpacing: 1.4,
        color: 'var(--evi-text-mute)',
        textTransform: 'uppercase',
        fontWeight: 600,
        marginBottom: 8,
      }}
    >
      {children}
    </div>
  );
}

function Grid({
  items,
  currentStyle,
  currentSeed,
  pending,
  onSelect,
}: {
  items: PickerItem[];
  currentStyle: AvatarStyle;
  currentSeed: string;
  pending: string | null;
  onSelect: (p: PickerItem) => void;
}) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: 10,
      }}
    >
      {items.map((item) => {
        const selected = item.style === currentStyle && item.seed === currentSeed;
        const isPending = pending === item.id;
        const border = selected
          ? '3px solid var(--evi-accent)'
          : item.generated
            ? '2px dashed var(--evi-accent)'
            : '2px solid var(--evi-border)';
        return (
          <button
            key={item.id}
            type="button"
            onClick={() => onSelect(item)}
            disabled={isPending}
            title={item.name}
            aria-pressed={selected}
            style={{
              aspectRatio: '1 / 1',
              borderRadius: '50%',
              padding: 0,
              border,
              background: 'var(--evi-surface-2)',
              cursor: isPending ? 'wait' : 'pointer',
              overflow: 'hidden',
              position: 'relative',
              transition: 'border-color 0.15s ease, transform 0.15s ease',
              opacity: isPending ? 0.6 : 1,
            }}
          >
            <img
              src={buildAvatarUrl(item.style, item.seed)}
              alt={item.name}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
            {selected && (
              <span
                aria-hidden
                style={{
                  position: 'absolute',
                  bottom: 4,
                  right: 4,
                  width: 22,
                  height: 22,
                  borderRadius: '50%',
                  background: 'var(--evi-accent)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'var(--color-brand-tx, #000)',
                  fontSize: 12,
                  fontWeight: 700,
                }}
              >
                ✓
              </span>
            )}
            {item.generated && !selected && (
              <span
                aria-hidden
                style={{
                  position: 'absolute',
                  top: 4,
                  right: 4,
                  width: 18,
                  height: 18,
                  borderRadius: '50%',
                  background: 'var(--evi-accent)',
                  color: 'var(--color-brand-tx, #000)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <SparkleIcon />
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

function ShuffleIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="14"
      height="14"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M16 3h5v5" />
      <path d="M4 20 L21 3" />
      <path d="M21 16v5h-5" />
      <path d="M15 15 L21 21" />
      <path d="M4 4 L9 9" />
    </svg>
  );
}

function SparkleIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="10"
      height="10"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M12 3 L13 8 L18 9 L13 10 L12 15 L11 10 L6 9 L11 8 Z" />
    </svg>
  );
}
